/**
 * CustomEmojiNode - Phase 2 Implementation
 *
 * Inline decorator node for Telegram custom emoji. Custom emoji require
 * Telegram Premium and are less commonly used. This is a placeholder
 * implementation that will be completed in Phase 2.
 */
import {
  DecoratorNode,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type DOMConversionMap,
  type DOMExportOutput,
  type LexicalEditor,
  type Spread,
} from 'lexical';
import type { JSX } from 'react';

export type SerializedCustomEmojiNode = Spread<
  {
    type: 'custom-emoji';
    emoji_id: string;
    fallback: string;
    version: 1;
  },
  SerializedLexicalNode
>;

export class CustomEmojiNode extends DecoratorNode<JSX.Element | null> {
  __emojiId: string;
  __fallback: string;

  static getType(): string {
    return 'custom-emoji';
  }

  static clone(node: CustomEmojiNode): CustomEmojiNode {
    return new CustomEmojiNode(node.__emojiId, node.__fallback, node.__key);
  }

  constructor(emojiId: string, fallback: string, key?: NodeKey) {
    super(key);
    this.__emojiId = emojiId;
    this.__fallback = fallback;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    span.className = (config.theme.customEmoji as string | undefined) ?? 'tg-custom-emoji';
    span.setAttribute('data-emoji-id', this.__emojiId);
    span.textContent = this.__fallback;
    return span;
  }

  updateDOM(prevNode: CustomEmojiNode, dom: HTMLElement): boolean {
    if (prevNode.__emojiId !== this.__emojiId) {
      dom.setAttribute('data-emoji-id', this.__emojiId);
      return true;
    }
    if (prevNode.__fallback !== this.__fallback) {
      dom.textContent = this.__fallback;
      return true;
    }
    return false;
  }

  decorate(): JSX.Element | null {
    // Phase 2: Render custom emoji sticker image
    return null;
  }

  exportJSON(): SerializedCustomEmojiNode {
    return {
      type: 'custom-emoji',
      emoji_id: this.__emojiId,
      fallback: this.__fallback,
      version: 1,
    };
  }

  static importJSON(serializedNode: SerializedCustomEmojiNode): CustomEmojiNode {
    return $createCustomEmojiNode(
      serializedNode.emoji_id,
      serializedNode.fallback
    );
  }

  exportDOM(_editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement('tg-emoji');
    element.setAttribute('emoji-id', this.__emojiId);
    element.textContent = this.__fallback;
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      'tg-emoji': () => ({
        conversion: (domNode: HTMLElement) => {
          const emojiId = domNode.getAttribute('emoji-id') || '';
          const fallback = domNode.textContent || '';
          return { node: $createCustomEmojiNode(emojiId, fallback) };
        },
        priority: 1,
      }),
    };
  }

  isInline(): boolean {
    return true;
  }
}

export function $createCustomEmojiNode(
  emojiId: string,
  fallback: string
): CustomEmojiNode {
  return new CustomEmojiNode(emojiId, fallback);
}

export function $isCustomEmojiNode(
  node: LexicalNode | null | undefined
): node is CustomEmojiNode {
  return node instanceof CustomEmojiNode;
}
